import React, { Component } from 'react';
import { FormGroup, Label, Input, Button, Row, Col, Table } from 'reactstrap';
import { EXPIRY_MAIL_PRESETS } from './constants';

const STEPS = ['Basic Information', 'Expiry Notification'];

class EmailTemplateForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            template: props.initialData,
            newEmail: '',
            errors: {}
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.initialData !== prevProps.initialData) {
            this.setState({ template: this.props.initialData, errors: {} });
        }
    }

    handleChange = (field, value) => {
        this.setState({
            template: {
                ...this.state.template,
                [field]: value
            }
        });
    }

    handleExpiryChange = (field, value) => {
        this.setState({
            template: {
                ...this.state.template,
                expiryConfig: {
                    ...this.state.template.expiryConfig,
                    [field]: value
                }
            }
        });
    }

    handleAddEmail = () => {
        const email = this.state.newEmail.trim();
        if (email && this.validateEmail(email)) {
            this.setState({
                template: {
                    ...this.state.template,
                    emails: [...(this.state.template.emails || []), email]
                },
                newEmail: ''
            });
        }
    }

    handleRemoveEmail = (index) => {
        this.setState({
            template: {
                ...this.state.template,
                emails: this.state.template.emails.filter((_, i) => i !== index)
            }
        });
    }

    handleAddExpiryMail = () => {
        this.setState({
            template: {
                ...this.state.template,
                expiryConfig: {
                    ...this.state.template.expiryConfig,
                    mails: [...(this.state.template.expiryConfig.mails || []), { days: 0, enabled: true }]
                }
            }
        });
    }

    handleRemoveExpiryMail = (index) => {
        this.setState({
            template: {
                ...this.state.template,
                expiryConfig: {
                    ...this.state.template.expiryConfig,
                    mails: this.state.template.expiryConfig.mails.filter((_, i) => i !== index)
                }
            }
        });
    }

    handleExpiryMailChange = (index, field, value) => {
        const updatedMails = [...this.state.template.expiryConfig.mails];
        updatedMails[index] = {
            ...updatedMails[index],
            [field]: field === 'days' ? (parseInt(value, 10) || 0) : value
        };
        this.setState({
            template: {
                ...this.state.template,
                expiryConfig: {
                    ...this.state.template.expiryConfig,
                    mails: updatedMails
                }
            }
        });
    }

    handlePresetSelect = (index, days) => {
        this.handleExpiryMailChange(index, 'days', days);
    }

    validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    handleEmailKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.handleAddEmail();
        }
    }

    validateStep = (step) => {
        const errors = {};
        const { template } = this.state;

        if (step === 0) {
            if (!template.name || template.name.trim() === '') {
                errors.name = 'Template name is required';
            }
        } else if (step === 1 && template.expiryConfig.enabled) {
            if (!template.expiryConfig.mails || template.expiryConfig.mails.length === 0) {
                errors.expiry = 'At least one expiry schedule is required when enabled';
            }
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    }

    validateForm = () => {
        const errors = {};
        const { template } = this.state;

        if (!template.name || template.name.trim() === '') {
            errors.name = 'Template name is required';
        }
        if (template.expiryConfig.enabled) {
            if (!template.expiryConfig.mails || template.expiryConfig.mails.length === 0) {
                errors.expiry = 'At least one expiry schedule is required when enabled';
            }
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    }

    handleNext = () => {
        if (this.validateStep(this.props.currentStep)) {
            this.props.onNextStep();
        }
    }

    handleCreateClick = () => {
        if (this.props.currentStep !== STEPS.length - 1) return;
        if (this.validateForm()) {
            this.props.onSave(this.state.template);
        }
    }

    handleNativeSubmit = (e) => {
        e.preventDefault();
    }

    renderStepIndicator = () => {
        const { currentStep } = this.props;
        return (
            <div className="stepper">
                {STEPS.map((label, index) => {
                    const isDone = index < currentStep;
                    const isActive = index === currentStep;
                    return (
                        <React.Fragment key={label}>
                            <div className={`stepper-item ${isActive ? 'active' : ''} ${isDone ? 'done' : ''}`}>
                                <div className="stepper-circle">
                                    {isDone ? <i className="fa fa-check" /> : <span>{index + 1}</span>}
                                </div>
                                <div className="stepper-label">{label}</div>
                            </div>
                            {index < STEPS.length - 1 && (
                                <div className={`stepper-connector ${index < currentStep ? 'done' : ''}`}>
                                    <div className="stepper-connector-fill" style={{ width: index < currentStep ? '100%' : '0%' }} />
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    }

    renderBasicInfo = (template, newEmail, errors) => {
        return (
            <div className="template-section">
                <FormGroup>
                    <Label>Template Name *</Label>
                    <Input
                        type="text"
                        placeholder="Enter template name"
                        value={template.name}
                        onChange={(e) => this.handleChange('name', e.target.value)}
                        invalid={!!errors.name}
                    />
                    {errors.name && <div className="text-danger small">{errors.name}</div>}
                </FormGroup>
                <Label className="section-subheading">Common Emails</Label>
                <p className="text-muted small mb-2">
                    These emails will receive notifications along with the Sales Associate
                </p>
                <Row>
                    <Col sm="8">
                        <Input
                            type="email"
                            placeholder="Enter email address and press Add"
                            value={newEmail}
                            onKeyDown={this.handleEmailKeyDown}
                            onChange={(e) => this.setState({ newEmail: e.target.value })}
                        />
                    </Col>
                    <Col sm="3">
                        <Button type="button" color="" block onClick={this.handleAddEmail}>
                            <i className="fa fa-plus" />
                        </Button>
                    </Col>
                </Row>
                <div className="email-list mt-2">
                    {(template.emails || []).map((email, index) => (
                        <span key={index} className="email-tag">
                            {email}
                            <i className="fa fa-times remove-email" onClick={() => this.handleRemoveEmail(index)} />
                        </span>
                    ))}
                    {(!template.emails || template.emails.length === 0) && (
                        <span className="text-muted small">No emails added</span>
                    )}
                </div>
            </div>
        );
    }

    renderExpiryConfig = (template, errors) => {
        return (
            <div className="template-section">
                <FormGroup check className="mb-2">
                    <Label check>
                        <Input
                            type="checkbox"
                            checked={template.expiryConfig.enabled}
                            onChange={(e) => this.handleExpiryChange('enabled', e.target.checked)}
                        />
                        Enable expiry notifications
                    </Label>
                </FormGroup>

                {template.expiryConfig.enabled && (
                    <div className="schedule-table-wrap mt-3">
                        <Label className="section-subheading mb-2">Schedule Mails</Label>
                        <Table bordered className="wizard-table schedule-table mb-2">
                            <thead>
                                <tr>
                                    <th>Schedule</th>
                                    <th className="text-center">Days</th>
                                    <th className="text-center">Enabled</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(template.expiryConfig.mails || []).map((mail, index) => {
                                    const matchedPreset = EXPIRY_MAIL_PRESETS.find(p => p.days === mail.days);
                                    return (
                                        <tr key={index}>
                                            <td>
                                                <Input
                                                    type="select"
                                                    className="schedule-select"
                                                    value={matchedPreset ? mail.days : 'custom'}
                                                    onChange={(e) => {
                                                        if (e.target.value === 'custom') {
                                                            return;
                                                        }
                                                        this.handlePresetSelect(index, parseInt(e.target.value, 10));
                                                    }}
                                                >
                                                    {EXPIRY_MAIL_PRESETS.map(preset => (
                                                        <option key={preset.days} value={preset.days}>
                                                            {preset.label}
                                                        </option>
                                                    ))}
                                                    {!matchedPreset && (
                                                        <option value="custom">
                                                            Custom: {mail.days} days
                                                        </option>
                                                    )}
                                                </Input>
                                            </td>
                                            <td className="text-center">
                                                <Input
                                                    type="number"
                                                    className="days-input"
                                                    value={mail.days}
                                                    onChange={(e) => this.handleExpiryMailChange(index, 'days', e.target.value)}
                                                />
                                            </td>
                                            <td className="text-center">
                                                <Input
                                                    type="checkbox"
                                                    className="schedule-checkbox"
                                                    checked={mail.enabled}
                                                    onChange={(e) => this.handleExpiryMailChange(index, 'enabled', e.target.checked)}
                                                />
                                            </td>
                                            <td className="text-center">
                                                <Button
                                                    type="button"
                                                    color="link"
                                                    className="text-danger schedule-remove"
                                                    onClick={() => this.handleRemoveExpiryMail(index)}
                                                    disabled={template.expiryConfig.mails.length <= 1}
                                                >
                                                    <i className="fa fa-trash" /> Remove
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                        <Button type="button" color="link" className="p-0" onClick={this.handleAddExpiryMail}>
                            <i className="fa fa-plus" /> Add Schedule
                        </Button>
                        {errors.expiry && <div className="text-danger small mt-1">{errors.expiry}</div>}
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { loading, isEditMode, onCancel, currentStep } = this.props;
        const { template, newEmail, errors } = this.state;
        const isLastStep = currentStep === STEPS.length - 1;

        return (
            <form onSubmit={this.handleNativeSubmit}>
                {this.renderStepIndicator()}
                <div className="step-content">
                    {currentStep === 0
                        ? this.renderBasicInfo(template, newEmail, errors)
                        : this.renderExpiryConfig(template, errors)}
                </div>
                <div className="template-actions d-flex justify-content-between mt-4">
                    <Button type="button" color="link" className="text-muted" onClick={onCancel}>
                        Cancel
                    </Button>
                    <div>
                        {currentStep > 0 && (
                            <Button type="button" color="secondary" outline onClick={this.props.onPrevStep} className="mr-2">
                                <i className="fa fa-chevron-left" /> Previous
                            </Button>
                        )}
                        {!isLastStep ? (
                            <Button type="button" color="primary" onClick={this.handleNext}>
                                Next <i className="fa fa-chevron-right" />
                            </Button>
                        ) : (
                            <Button type="button" color="primary" disabled={loading} onClick={this.handleCreateClick}>
                                {loading ? 'Saving...' : (isEditMode ? 'Update Template' : 'Create Template')}
                            </Button>
                        )}
                    </div>
                </div>
            </form>
        );
    }
}

export default EmailTemplateForm;
